import { useEffect, useState, useRef, useCallback } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Clapperboard,
  Loader2,
  Check,
  Download,
  Pencil,
  Share2,
  ChevronDown,
  ChevronUp,
  Clock,
  Globe2,
  Layers,
  AlertCircle,
  Play,
  Pause,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { appFetch, appApiBaseUrl } from '@/shared/api/app-client'
import type {
  ApiResponse,
  ClippingJobResult,
  ClipCandidate,
  Template,
  TranscriptSentence,
  Variant,
} from '@/shared/api/app-types'
import { variantToClipCandidate } from '@/shared/api/app-types'
import { cn } from '@/shared/ui/cn'
import {
  renderClipToMp4,
  getClipFileName,
  ensureClipWorkspaceRoot,
  buildClipProjectForEditor,
  type ClipRenderConfig,
} from '@/features/app-shell/utils/clip-render'
import { triggerDownload } from '@/features/assistant/tools/export-tools'
import { WorkspacePickerDialog } from '@/features/app-shell/components/workspace-picker-dialog'
import { PublishClipDialog } from '@/features/app-shell/components/publish-clip-dialog'
import { buildCaptionRenderConfig, getCaptionStyleConfig } from '@/features/app-shell/components/caption-style-config'

/* ------------------------------------------------------------------ */
/* 工具函数                                                            */
/* ------------------------------------------------------------------ */

function formatMs(ms: number): string {
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return m > 0 ? `${m}分${s}秒` : `${s}秒`
}

/** 从转录中过滤出与某个时间范围重叠的句子（不要求完全包含） */
function getSentencesInRange(
  sentences: TranscriptSentence[],
  startMs: number,
  endMs: number,
): TranscriptSentence[] {
  // 重叠检测：句子的 [startMs, endMs] 与片段的 [startMs-500, endMs+500] 有交集即可。
  // 之前用全包含检测（s.startMs >= startMs && s.endMs <= endMs）会丢掉
  // 跨越片段边界的句子，导致片段只显示一两条字幕。
  return sentences.filter(
    (s) => s.startMs <= endMs + 500 && s.endMs >= startMs - 500,
  )
}

/** 将 /uploads/... 相对路径补全为后端完整 URL */
function resolveSourceUrl(src: string): string {
  if (src.startsWith('/uploads/')) {
    return `${appApiBaseUrl}${src}`
  }
  return src
}

type TemplatePreviewAssets = {
  backgroundEnabled?: boolean
  background?: { publicUrl?: string } | null
  logoEnabled?: boolean
  logos?: Array<{
    publicUrl?: string
    fileName?: string
    position?: string
    scale?: string
    x?: number
    y?: number
    width?: number
    height?: number
  }>
  captions?: { enabled?: boolean; x?: number; y?: number; width?: number }
  intro?: { publicUrl?: string } | null
  outro?: { publicUrl?: string } | null
}

function getLogoPosition(position?: string): { left: number; top: number } {
  switch (position) {
    case 'top-left': return { left: 5, top: 5 }
    case 'top-right': return { left: 85, top: 5 }
    case 'bottom-left': return { left: 5, top: 85 }
    case 'center': return { left: 42, top: 45 }
    case 'bottom-right':
    default: return { left: 85, top: 85 }
  }
}

function getLogoWidth(scale?: string): number {
  return ({ small: 8, medium: 12, large: 18 } as Record<string, number>)[scale ?? 'medium'] ?? 12
}

/* ------------------------------------------------------------------ */
/* 片段范围播放器                                                      */
/* ------------------------------------------------------------------ */

export function ClipRangePlayer({
  src,
  startMs,
  endMs,
  sentences,
  config,
}: {
  src: string
  startMs: number
  endMs: number
  sentences: TranscriptSentence[]
  config: ClipRenderConfig
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)
  const [ready, setReady] = useState(false)
  const [currentMs, setCurrentMs] = useState(startMs)
  const [segment, setSegment] = useState<'intro' | 'main' | 'outro'>('main')

  const startSec = startMs / 1000
  const endSec = endMs / 1000
  const template = config.templateConfig as TemplatePreviewAssets | null
  const introSrc = template?.intro?.publicUrl ? resolveSourceUrl(template.intro.publicUrl) : null
  const outroSrc = template?.outro?.publicUrl ? resolveSourceUrl(template.outro.publicUrl) : null
  const activeSrc = segment === 'intro' ? introSrc : segment === 'outro' ? outroSrc : src

  useEffect(() => {
    setSegment(introSrc ? 'intro' : 'main')
    setCurrentMs(startMs)
    setReady(false)
  }, [introSrc, startMs])

  const handleLoadedMetadata = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    try {
      v.currentTime = segment === 'main' ? startSec : 0
      setReady(true)
      if (playing) void v.play().catch(() => setPlaying(false))
    } catch {
      // 忽略
    }
  }, [playing, segment, startSec])

  const handleTimeUpdate = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    if (segment !== 'main') return
    setCurrentMs(Math.round(v.currentTime * 1000))
    // 超出片段范围 → 回到起点暂停
    if (v.currentTime >= endSec) {
      if (outroSrc) {
        setSegment('outro')
        setReady(false)
      } else {
        v.pause()
        v.currentTime = startSec
        setPlaying(false)
      }
    }
  }, [endSec, outroSrc, segment, startSec])

  const logos = template?.logoEnabled !== false ? template?.logos ?? [] : []
  // 未选择字幕样式时，转录文本仅用于分析与结果说明，不应叠加到预览或导出视频中。
  const captionsEnabled = Boolean(config.style) && template?.captions?.enabled !== false
  const activeCaption = captionsEnabled && segment === 'main'
    ? sentences.find((sentence) => sentence.startMs <= currentMs && sentence.endMs >= currentMs)?.text ?? ''
    : ''
  const captionStyle = buildCaptionRenderConfig(
    getCaptionStyleConfig(config.style ?? 'blue'),
    config.captionConfig ?? {},
  )
  // 结果页预览没有逐词动画；对 fill 高亮样式，用模板高亮色显示当前字幕，
  // 让预览至少保留与导出/编辑器一致的主色与描边语义。
  const previewCaptionColor =
    captionStyle.highlightMode === 'fill' && captionStyle.highlightColor
      ? captionStyle.highlightColor
      : captionStyle.color

  const handleEnded = useCallback(() => {
    if (segment === 'intro') {
      setSegment('main')
      setReady(false)
      return
    }
    if (segment === 'outro') {
      setPlaying(false)
      setSegment(introSrc ? 'intro' : 'main')
      setReady(false)
    }
  }, [introSrc, segment])

  const togglePlay = useCallback(() => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) {
      if (segment === 'main' && v.currentTime >= endSec) {
        setSegment(outroSrc ? 'outro' : introSrc ? 'intro' : 'main')
        setReady(false)
      }
      // 如果在范围外，先回到起点
      if (segment === 'main' && (v.currentTime < startSec || v.currentTime >= endSec)) {
        v.currentTime = startSec
      }
      void v.play().catch(() => setPlaying(false))
      setPlaying(true)
    } else {
      v.pause()
      setPlaying(false)
    }
  }, [endSec, introSrc, outroSrc, segment, startSec])

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black">
      <video
        key={activeSrc}
        ref={videoRef}
        src={activeSrc ?? src}
        className="relative z-10 h-full w-full object-contain"
        playsInline
        preload="metadata"
        crossOrigin="anonymous"
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
      />
      {template?.backgroundEnabled !== false && template?.background?.publicUrl ? (
        <img
          src={resolveSourceUrl(template.background.publicUrl)}
          alt=""
          className="pointer-events-none absolute inset-0 z-0 size-full object-cover"
        />
      ) : null}
      <div className="pointer-events-none absolute inset-0 z-20">
        {logos.map((logo, index) => {
          if (!logo.publicUrl) return null
          const fallback = getLogoPosition(logo.position)
          return (
            <img
              key={`${logo.publicUrl}-${index}`}
              src={resolveSourceUrl(logo.publicUrl)}
              alt={logo.fileName ?? '品牌 logo'}
              className="absolute object-contain"
              style={{
                left: `${logo.x ?? fallback.left}%`,
                top: `${logo.y ?? fallback.top}%`,
                width: `${logo.width ?? getLogoWidth(logo.scale)}%`,
                height: logo.height ? `${logo.height}%` : undefined,
              }}
            />
          )
        })}
        {activeCaption ? (
          <div
            className="absolute text-center"
            style={{
              left: `${template?.captions?.x ?? 10}%`,
              top: `${template?.captions?.y ?? 70}%`,
              width: `${template?.captions?.width ?? 80}%`,
              color: previewCaptionColor,
              fontFamily: captionStyle.fontFamily,
              fontSize: `${Math.max(18, captionStyle.fontSize * 0.48)}px`,
              fontWeight: captionStyle.fontWeight,
              fontStyle: captionStyle.italic ? 'italic' : 'normal',
              lineHeight: captionStyle.lineHeight,
              letterSpacing: `${captionStyle.letterSpacing}em`,
              WebkitTextStroke: captionStyle.strokeWidth > 0
                ? `${Math.min(1.5, Math.max(0.75, captionStyle.strokeWidth * 0.18))}px ${captionStyle.strokeColor}`
                : undefined,
              textShadow: captionStyle.textShadow,
              background: captionStyle.backgroundColor,
            }}
          >
            {activeCaption}
          </div>
        ) : null}
      </div>
      {/* 播放按钮覆盖层 */}
      <button
        type="button"
        onClick={togglePlay}
        className="absolute inset-0 z-30 flex items-center justify-center bg-black/20 transition-colors hover:bg-black/30"
      >
        <div className="flex size-12 items-center justify-center rounded-full bg-white/90 shadow-lg">
          {playing ? (
            <Pause className="size-5 text-black" />
          ) : (
            <Play className="ml-0.5 size-5 text-black" />
          )}
        </div>
      </button>
      {/* 时间范围标签 */}
      <div className="absolute bottom-2 right-2 z-30 rounded bg-black/60 px-2 py-0.5 text-xs text-white">
        {formatMs(startMs)} - {formatMs(endMs)}
      </div>
      {!ready ? (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/40">
          <Loader2 className="size-5 animate-spin text-white/70" />
        </div>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* 片段卡片                                                            */
/* ------------------------------------------------------------------ */

interface PendingEdit {
  clip: ClipCandidate
  sentences: TranscriptSentence[]
  words: Array<{ text: string; startMs: number; endMs: number; sentenceId: number }> | undefined
}

interface PendingPublish {
  clip: ClipCandidate
  variantId: number | null
}

function ClipCard({
  clip,
  index,
  sentences,
  words,
  config,
  onEdit, // 由父组件提供，处理 workspace 选择 + 项目创建 + 跳转
  editBusy, // 父组件控制的编辑 loading 状态
  editError, // 父组件控制的编辑错误
  onPublish,
}: {
  clip: ClipCandidate
  index: number
  sentences: TranscriptSentence[]
  words?: Array<{ text: string; startMs: number; endMs: number; sentenceId: number }>
  config: ClipRenderConfig
  onEdit: (clip: ClipCandidate, sentences: TranscriptSentence[], words?: Array<{ text: string; startMs: number; endMs: number; sentenceId: number }>) => void
  editBusy: boolean
  editError: string | null
  onPublish?: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [tab, setTab] = useState<'transcript' | 'caption'>('transcript')
  const [selected, setSelected] = useState(false)

  // 下载状态（下载不依赖 workspace，在卡片内独立处理）
  const [downloading, setDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState(0)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const clipSentences = getSentencesInRange(sentences, clip.startMs, clip.endMs)
  const transcriptText = clipSentences.map((s) => s.text).join(' ')
  const durationSec = Math.round((clip.endMs - clip.startMs) / 1000)
  const scoreColor =
    clip.score >= 8
      ? 'text-emerald-500'
      : clip.score >= 6
        ? 'text-amber-500'
        : 'text-muted-foreground'

  const busy = editBusy || downloading
  const resolvedSrc = resolveSourceUrl(config.src)

  async function handleDownload() {
    if (busy) return
    setDownloading(true)
    setDownloadError(null)
    setDownloadProgress(0)
    try {
      const result = await renderClipToMp4(clip, sentences, config, {
        onProgress: (progress) => {
          setDownloadProgress(progress.progress)
        },
        words,
      })
      const fileName = getClipFileName(clip)
      triggerDownload(result.blob, fileName)
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : '渲染下载失败')
    } finally {
      setDownloading(false)
      setDownloadProgress(0)
    }
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border bg-card transition-colors',
        selected ? 'border-primary' : 'border-border',
      )}
    >
      {/* 片段预览播放器 */}
      {config.src ? (
        <div className="p-4 sm:p-5">
          <ClipRangePlayer
            src={resolvedSrc}
            startMs={clip.startMs}
            endMs={clip.endMs}
            sentences={clipSentences}
            config={config}
          />
        </div>
      ) : null}

      {/* 头部：序号 + 标题 + 评分 */}
      <div className="flex items-start gap-4 px-4 pb-3 sm:px-5">
        {/* 选择框 */}
        <button
          type="button"
          onClick={() => setSelected(!selected)}
          disabled={busy}
          className={cn(
            'mt-1 flex size-5 shrink-0 items-center justify-center rounded border transition-colors',
            selected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border hover:border-primary',
          )}
        >
          {selected ? <Check className="size-3" /> : null}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-muted-foreground">
              #{index + 1}
            </span>
            <h3 className="truncate text-base font-semibold text-foreground">
              {clip.title}
            </h3>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="size-3" />
              {durationSec}秒
            </span>
            <span>·</span>
            <span>{formatMs(clip.startMs)} - {formatMs(clip.endMs)}</span>
          </div>
        </div>

        {/* 评分 */}
        <div className="shrink-0 text-right">
          <div className={cn('text-2xl font-bold', scoreColor)}>
            {clip.score.toFixed(1)}
          </div>
          <div className="text-[10px] text-muted-foreground">/10</div>
        </div>
      </div>

      {/* 评分理由 */}
      <div className="px-4 pb-3 sm:px-5">
        <div className="text-xs font-medium text-muted-foreground">
          传播力评分理由：
        </div>
        <p
          className={cn(
            'mt-1 text-sm leading-6 text-foreground/80',
            !expanded && 'line-clamp-2',
          )}
        >
          {clip.reason}
        </p>
        {clip.reason.length > 60 ? (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="mt-1 flex items-center gap-0.5 text-xs text-primary hover:underline"
          >
            {expanded ? (
              <>收起 <ChevronUp className="size-3" /></>
            ) : (
              <>展开 <ChevronDown className="size-3" /></>
            )}
          </button>
        ) : null}
      </div>

      {/* 转录/字幕 tab */}
      <div className="border-t border-border px-4 py-3 sm:px-5">
        <div className="flex gap-4 border-b border-border pb-2">
          <button
            type="button"
            onClick={() => setTab('transcript')}
            className={cn(
              'pb-1 text-xs font-medium transition-colors',
              tab === 'transcript'
                ? 'border-b-2 border-foreground text-foreground'
                : 'text-muted-foreground',
            )}
          >
            转录文本
          </button>
          <button
            type="button"
            onClick={() => setTab('caption')}
            className={cn(
              'pb-1 text-xs font-medium transition-colors',
              tab === 'caption'
                ? 'border-b-2 border-foreground text-foreground'
                : 'text-muted-foreground',
            )}
          >
            社交文案
          </button>
        </div>
        <p className="mt-3 text-sm leading-7 text-muted-foreground">
          {tab === 'transcript'
            ? transcriptText || '暂无转录文本'
            : `${clip.title} — ${clip.reason.slice(0, 30)}...`}
        </p>
      </div>

      {/* 错误提示 */}
      {editError ? (
        <div className="mx-4 mb-3 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 sm:mx-5">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
          <p className="text-xs text-destructive">{editError}</p>
        </div>
      ) : null}
      {downloadError ? (
        <div className="mx-4 mb-3 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 sm:mx-5">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
          <p className="text-xs text-destructive">{downloadError}</p>
        </div>
      ) : null}

      {/* 底部按钮 */}
      <div className="flex items-center justify-between border-t border-border px-4 py-3 sm:px-5">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-600">
            <Check className="size-3" />
            可分享
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={() => onEdit(clip, sentences, words)}
            disabled={busy}
          >
            {editBusy ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Pencil className="size-3.5" />
            )}
            {editBusy ? '准备中…' : '编辑'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={handleDownload}
            disabled={busy}
          >
            {downloading ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Download className="size-3.5" />
            )}
            {downloading ? `渲染 ${downloadProgress}%` : '下载'}
          </Button>
          <Button size="sm" className="h-8 gap-1.5 text-xs" disabled={busy || !onPublish} onClick={onPublish}>
            <Share2 className="size-3.5" />
            分享
          </Button>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* 主组件                                                              */
/* ------------------------------------------------------------------ */

export function ClipResultsCanvas({
  taskId,
  title,
  src,
  style,
  orientation,
  resolution,
}: {
  taskId: string
  title?: string
  src?: string
  style?: string
  orientation?: string
  resolution?: string
}) {
  const navigate = useNavigate()
  const [job, setJob] = useState<ClippingJobResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectAll, setSelectAll] = useState(false)
  const [sortBy, setSortBy] = useState<'score' | 'time'>('score')
  const [brandTemplate, setBrandTemplate] = useState<Template | null>(null)

  // 工作空间选择弹窗 + 编辑状态
  const [workspacePickerOpen, setWorkspacePickerOpen] = useState(false)
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null)
  const [editBusy, setEditBusy] = useState(false)
  const [editingClipStartMs, setEditingClipStartMs] = useState<number | null>(null)
  const [editError, setEditError] = useState<string | null>(null)
  const [pendingPublish, setPendingPublish] = useState<PendingPublish | null>(null)
  const [clipVariants, setClipVariants] = useState<Variant[]>([])

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const res = await appFetch<ApiResponse<ClippingJobResult>>(
          `/api/clipping/jobs/${taskId}`,
        )
        if (!cancelled) {
          setJob(res.data)
          setLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '加载结果失败')
          setLoading(false)
        }
      }
    }
    load()
    return () => { cancelled = true }
  }, [taskId])

  useEffect(() => {
    if (!job?.projectId) {
      setClipVariants([])
      return
    }
    let cancelled = false
    void appFetch<ApiResponse<{ items: Variant[] }>>(`/api/projects/${job.projectId}/variants?variantType=clip`)
      .then((response) => { if (!cancelled) setClipVariants(response.data.items) })
      .catch(() => { if (!cancelled) setClipVariants([]) })
    return () => { cancelled = true }
  }, [job?.projectId])

  useEffect(() => {
    const templateId = job?.brandTemplateId
    if (!templateId) {
      setBrandTemplate(null)
      return
    }

    let cancelled = false
    void appFetch<ApiResponse<Template>>(`/api/templates/${templateId}`)
      .then((res) => {
        if (!cancelled) {
          setBrandTemplate(res.data)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBrandTemplate(null)
        }
      })

    return () => {
      cancelled = true
    }
  }, [job?.brandTemplateId])

  const clips = job?.clips ?? []
  const transcript = job?.transcript

  function variantIdForClip(clip: ClipCandidate) {
    return clipVariants.find((variant) => {
      const candidate = variantToClipCandidate(variant)
      return candidate.startMs === clip.startMs && candidate.endMs === clip.endMs
    })?.id ?? null
  }

  // 排序
  const sortedClips = [...clips].sort((a, b) => {
    if (sortBy === 'score') return b.score - a.score
    return a.startMs - b.startMs
  })

  // 构建片段渲染配置：优先从 job 持久化数据读取，回退到 URL search params
  const clipConfig: ClipRenderConfig = {
    src: job?.videoUrl ?? src ?? '',
    style: (brandTemplate?.config?.captionStyleKey as string | undefined) ?? job?.captionStyle ?? style,
    orientation: job?.orientation ?? orientation,
    resolution: job?.resolution ?? resolution,
    brandTemplateId: brandTemplate?.id ?? job?.brandTemplateId ?? null,
    brandTemplateName: brandTemplate?.templateName ?? job?.brandTemplateName ?? null,
    captionConfig: (brandTemplate?.config?.captions as Record<string, unknown> | undefined) ?? null,
    templateConfig: brandTemplate?.config ?? null,
  }

  // 执行编辑：确保 workspace → 创建项目 → 跳转编辑器
  const doEdit = useCallback(
    async (
      clip: ClipCandidate,
      sentences: TranscriptSentence[],
      words?: Array<{ text: string; startMs: number; endMs: number; sentenceId: number }>,
    ) => {
      setEditingClipStartMs(clip.startMs)
      setEditBusy(true)
      setEditError(null)
      try {
        const wsStatus = await ensureClipWorkspaceRoot()
        if (!wsStatus.ok) {
          // 需要选择工作空间 → 弹窗，保存 pending edit
          setPendingEdit({ clip, sentences, words })
          setWorkspacePickerOpen(true)
          setEditBusy(false)
          setEditingClipStartMs(null)
          return
        }
        const result = await buildClipProjectForEditor(clip, sentences, clipConfig, words, job?.projectId ?? undefined)
        await navigate({
          to: '/editor/$projectId',
          params: { projectId: result.projectId },
          search: { assistant: true },
        })
      } catch (err) {
        setEditError(err instanceof Error ? err.message : '打开编辑器失败')
        setEditBusy(false)
        setEditingClipStartMs(null)
      }
    },
    [clipConfig, navigate],
  )

  // 工作空间选择成功后，继续执行 pending edit
  const handleWorkspacePicked = useCallback(() => {
    if (pendingEdit) {
      void doEdit(pendingEdit.clip, pendingEdit.sentences, pendingEdit.words)
      setPendingEdit(null)
    }
  }, [pendingEdit, doEdit])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
          正在加载分析结果…
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background px-5 py-10">
        <Link
          to="/app"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-5" />
          返回
        </Link>
        <p className="mt-6 text-sm text-destructive">{error}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* 顶栏 */}
      <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/95 px-5 backdrop-blur">
        <Link
          to="/app"
          className="flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-5" />
        </Link>
        <span className="text-sm font-semibold text-foreground">Video Clips</span>
      </div>

      {/* 视频信息栏 */}
      <div className="mx-auto mt-6 flex max-w-[724px] flex-wrap items-start justify-between gap-2.5 rounded-2xl border border-border bg-card px-3 py-3 shadow-md sm:gap-6 sm:px-5">
        <div className="flex w-full items-start gap-3 sm:max-w-[65%]">
          <div className="flex h-[65px] w-[107px] shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
            {clipConfig.src ? (
              <video
                src={resolveSourceUrl(clipConfig.src)}
                className="h-full w-full object-cover"
                muted
                playsInline
                preload="metadata"
                crossOrigin="anonymous"
                onLoadedData={(event) => {
                  try {
                    event.currentTarget.currentTime = 0.1
                  } catch {
                    // 无法定位首帧时保留浏览器默认画面。
                  }
                }}
              />
            ) : <Clapperboard className="size-6 text-muted-foreground" />}
          </div>
          <div className="min-w-0 flex-1 pt-1">
            <div className="truncate text-sm font-medium text-foreground">
              {title ?? '视频片段'}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Globe2 className="size-3" />
                {transcript?.language ?? '—'}
              </span>
              {transcript?.durationMs ? (
                <span className="flex items-center gap-1">
                  <Clock className="size-3" />
                  {formatDuration(transcript.durationMs)}
                </span>
              ) : null}
              <span className="flex items-center gap-1">
                <Layers className="size-3" />
                {clips.length} 个片段
              </span>
              {clipConfig.brandTemplateName ? (
                <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-foreground/80">
                  模板: {clipConfig.brandTemplateName}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* 工具栏 */}
      <div className="mx-auto mt-6 flex max-w-[820px] items-center justify-between px-5">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setSelectAll(!selectAll)}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <div
              className={cn(
                'flex size-4 items-center justify-center rounded border transition-colors',
                selectAll
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border',
              )}
            >
              {selectAll ? <Check className="size-3" /> : null}
            </div>
            全选
            <span className="text-xs">({clips.length})</span>
          </button>
        </div>
        <div className="flex items-center gap-2">
          {job?.projectId ? (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() =>
                navigate({
                  to: '/app/projects/$projectId',
                  params: { projectId: String(job.projectId!) },
                })
              }
            >
              <Share2 className="size-3.5" />
              去分享
            </Button>
          ) : null}
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
            <Download className="size-3.5" />
            导出
          </Button>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'score' | 'time')}
            className="h-8 appearance-none rounded-full border border-border bg-card px-3 pr-7 text-xs font-medium text-muted-foreground focus:outline-none"
          >
            <option value="score">按评分排序</option>
            <option value="time">按时间排序</option>
          </select>
        </div>
      </div>

      {/* 片段列表 */}
      <div className="mx-auto max-w-[820px] space-y-4 px-5 pb-20 pt-4">
        {sortedClips.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <Clapperboard className="size-12 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {job?.error ?? '未找到高光片段'}
            </p>
          </div>
        ) : (
          sortedClips.map((clip, index) => (
            <ClipCard
              key={`${clip.startMs}-${index}`}
              clip={clip}
              index={index}
              sentences={transcript?.sentences ?? []}
              words={transcript?.words}
              config={clipConfig}
              onEdit={doEdit}
              editBusy={editBusy && editingClipStartMs === clip.startMs}
              editError={pendingEdit?.clip.startMs === clip.startMs ? editError : null}
              onPublish={() => setPendingPublish({ clip, variantId: variantIdForClip(clip) })}
            />
          ))
        )}
      </div>

      {/* 底部操作 */}
      <div className="mx-auto max-w-[820px] px-5 pb-10">
        <Button
          variant="outline"
          className="w-full rounded-full"
          onClick={() => navigate({ to: '/app' })}
        >
          返回首页
        </Button>
      </div>

      {/* 工作空间选择弹窗 */}
      <WorkspacePickerDialog
        open={workspacePickerOpen}
        onOpenChange={setWorkspacePickerOpen}
        onSuccess={handleWorkspacePicked}
      />
      <PublishClipDialog
        open={pendingPublish !== null}
        onOpenChange={(open) => { if (!open) setPendingPublish(null) }}
        title={pendingPublish?.clip.title ?? ''}
        reason={pendingPublish?.clip.reason}
        projectId={job?.projectId ?? null}
        variantId={pendingPublish?.variantId ?? null}
        generateShareVideo={async () => {
          if (!pendingPublish) throw new Error('未选择要发布的片段')
          const result = await renderClipToMp4(
            pendingPublish.clip,
            transcript?.sentences ?? [],
            clipConfig,
            { words: transcript?.words },
          )
          return result.blob
        }}
      />
      {editBusy ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-4 shadow-xl">
            <Loader2 className="size-5 animate-spin text-primary" />
            <div><div className="text-sm font-medium text-foreground">正在创建独立编辑项目</div><div className="mt-0.5 text-xs text-muted-foreground">导入素材并应用品牌模板…</div></div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
